import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCart } from '../commerce/CartContext';
import { formatCurrency } from '../data';
import {
  createOrder,
  getCheckoutConfiguration,
  submitGuestProof,
  uploadGuestProof,
  validateProof
} from '../services/checkoutService';

const {
  FiCheck,
  FiCreditCard,
  FiMapPin,
  FiPackage,
  FiUpload,
  FiHome,
  FiShoppingBag,
  FiRefreshCw,
  FiCopy
} = FiIcons;

const countryCodes = [
  ['Sri Lanka', '+94'],
  ['Bahrain', '+973'],
  ['India', '+91'],
  ['United Arab Emirates', '+971'],
  ['United Kingdom', '+44'],
  ['United States', '+1'],
  ['Australia', '+61'],
  ['Canada', '+1'],
  ['Singapore', '+65'],
  ['Malaysia', '+60']
];

const initialCustomer = {
  email: '',
  fullName: '',
  countryCode: '+94',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  deliveryNotes: ''
};

function parseBankInstructions(instructions) {
  if (!instructions) return null;

  const fields = [
    { key: 'bank_name', label: 'Bank Name', patterns: [/bank\s*name\s*[:\-]\s*(.+)/i] },
    { key: 'branch_name', label: 'Branch Name', patterns: [/branch\s*(?:name)?\s*[:\-]\s*(.+)/i] },
    { key: 'account_name', label: 'Account Name', patterns: [/account\s*name\s*[:\-]\s*(.+)/i] },
    { key: 'account_number', label: 'Account Number', patterns: [/account\s*(?:number|no|#|num)\s*[:\-]\s*(.+)/i] },
    { key: 'bank_code', label: 'Bank Code', patterns: [/bank\s*code\s*[:\-]\s*(.+)/i] },
    { key: 'branch_code', label: 'Branch Code', patterns: [/branch\s*code\s*[:\-]\s*(.+)/i] },
    { key: 'swift_code', label: 'SWIFT Code', patterns: [/swift\s*(?:code)?\s*[:\-]\s*(.+)/i] }
  ];

  const result = {};
  let hasMatch = false;
  const lines = instructions.split('\n');

  for (const field of fields) {
    for (const line of lines) {
      for (const pattern of field.patterns) {
        const match = line.match(pattern);
        if (match) {
          result[field.key] = { label: field.label, value: match[1].trim() };
          hasMatch = true;
          break;
        }
      }
      if (result[field.key]) break;
    }
  }

  return hasMatch ? result : null;
}

function BankInstructionsDisplay({ instructions }) {
  const [copiedField, setCopiedField] = useState('');
  const parsed = parseBankInstructions(instructions);

  const copyToClipboard = async (text, fieldKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(''), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(''), 2000);
    }
  };

  if (!parsed) {
    return (
      <div className="payment-instructions">
        <strong>Payment instructions</strong>
        <p>{instructions}</p>
      </div>
    );
  }

  return (
    <div className="payment-instructions">
      <strong>Payment instructions</strong>
      <div className="bank-details-grid">
        {Object.entries(parsed).map(([key, { label, value }]) => (
          <div className="bank-detail-row" key={key}>
            <span className="bank-detail-label">{label}:</span>
            <span className="bank-detail-value">{value}</span>
            <button
              type="button"
              className={`bank-copy-btn ${copiedField === key ? 'copied' : ''}`}
              onClick={() => copyToClipboard(value, key)}
              title={`Copy ${label}`}
            >
              {copiedField === key ? (
                <>
                  <SafeIcon icon={FiCheck} />
                  Copied
                </>
              ) : (
                <>
                  <SafeIcon icon={FiCopy} />
                  Copy
                </>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CheckoutProgress({ step }) {
  const steps = [[1, 'Delivery'], [2, 'Payment'], [3, 'Review']];
  return (
    <ol className="checkout-progress" aria-label="Checkout progress">
      {steps.map(([number, label]) => (
        <li className={step >= number ? 'active' : ''} key={number}>
          <span>{step > number ? <SafeIcon icon={FiCheck} /> : number}</span>
          <strong>{label}</strong>
        </li>
      ))}
    </ol>
  );
}

function DeliveryOptionSelector({ value, onChange, storePickup, onRefresh }) {
  return (
    <div className="delivery-option-selector">
      <label
        className={`delivery-option ${value === 'home' ? 'selected' : ''}`}
      >
        <input
          type="radio"
          name="deliveryOption"
          checked={value === 'home'}
          onChange={() => onChange('home')}
        />
        <div className="delivery-option-content">
          <SafeIcon icon={FiHome} />
          <div>
            <strong>Home Delivery</strong>
            <span>Delivered to your address</span>
          </div>
        </div>
      </label>

      {storePickup?.enabled && (
        <label
          className={`delivery-option ${value === 'pickup' ? 'selected' : ''}`}
        >
          <input
            type="radio"
            name="deliveryOption"
            checked={value === 'pickup'}
            onChange={() => onChange('pickup')}
          />
          <div className="delivery-option-content">
            <SafeIcon icon={FiShoppingBag} />
            <div>
              <strong>Store Pickup</strong>
              <span>Collect from our store</span>
            </div>
          </div>
        </label>
      )}

      {!storePickup?.enabled && onRefresh && (
        <button
          type="button"
          className="refresh-delivery-options"
          onClick={onRefresh}
          style={{
            marginTop: 12,
            padding: '8px 14px',
            fontSize: '0.6rem',
            color: 'var(--gold)',
            background: 'transparent',
            border: '1px solid rgba(201, 154, 69, 0.3)',
            cursor: 'pointer'
          }}
        >
          <SafeIcon icon={FiRefreshCw} style={{ marginRight: 6 }} />
          Refresh delivery options
        </button>
      )}
    </div>
  );
}

function CustomerStep({
  values,
  rates,
  deliveryOption,
  storePickup,
  onChange,
  onDeliveryOptionChange,
  onContinue,
  onRefreshConfig,
  onAutofill
}) {
  const isPickup = deliveryOption === 'pickup';

  return (
    <form className="checkout-card checkout-form" onSubmit={onContinue}>
      <div className="checkout-card-heading">
        <SafeIcon icon={FiMapPin} />
        <div>
          <span>Step 01</span>
          <h2>Delivery Details</h2>
        </div>
      </div>

      <DeliveryOptionSelector
        value={deliveryOption}
        onChange={onDeliveryOptionChange}
        storePickup={storePickup}
        onRefresh={onRefreshConfig}
      />

      <button
        type="button"
        onClick={onAutofill}
        style={{
          marginBottom: 22,
          padding: '10px 16px',
          color: 'var(--gold)',
          border: '1px solid rgba(201, 154, 69, 0.45)',
          background: 'rgba(201, 154, 69, 0.08)',
          cursor: 'pointer'
        }}
      >
        Autofill test checkout details
      </button>

      {isPickup && storePickup && (
        <div className="store-pickup-info">
          <h3>Pickup Location</h3>
          <p><strong>{storePickup.address}</strong></p>
          {storePickup.city && <p>{storePickup.city}</p>}
          {storePickup.phone && <p>Phone: {storePickup.phone}</p>}
          {storePickup.hours && <p>Hours: {storePickup.hours}</p>}
        </div>
      )}

      <div className="checkout-fields">
        <label>
          Email
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            value={values.email}
            onChange={onChange}
          />
        </label>

        {!isPickup && (
          <label>
            Full name
            <input
              name="fullName"
              autoComplete="name"
              required
              value={values.fullName}
              onChange={onChange}
            />
          </label>
        )}

        <label>
          Country code
          <select
            name="countryCode"
            value={values.countryCode}
            onChange={onChange}
            required
          >
            {countryCodes.map(([country, code]) => (
              <option value={code} key={`${country}-${code}`}>
                {country} ({code})
              </option>
            ))}
          </select>
        </label>

        <label>
          Phone number
          <input
            name="phone"
            type="tel"
            autoComplete="tel"
            required
            value={values.phone}
            onChange={onChange}
            placeholder="77 123 4567"
          />
        </label>

        {!isPickup && (
          <>
            <label className="checkout-field-wide">
              Address
              <input
                name="addressLine1"
                autoComplete="address-line1"
                required
                value={values.addressLine1}
                onChange={onChange}
              />
            </label>
            <label className="checkout-field-wide">
              Address line 2
              <input
                name="addressLine2"
                autoComplete="address-line2"
                value={values.addressLine2}
                onChange={onChange}
              />
            </label>
            <label>
              City
              <input
                name="city"
                autoComplete="address-level2"
                required
                value={values.city}
                onChange={onChange}
              />
            </label>
            <label>
              District
              <select
                name="region"
                required
                value={values.region}
                onChange={onChange}
              >
                <option value="">Select district</option>
                {rates.map((rate) => (
                  <option value={rate.id} key={rate.id}>
                    {rate.region} — {formatCurrency(rate.rate)}
                  </option>
                ))}
              </select>
            </label>
            <label className="checkout-field-wide">
              Delivery notes
              <textarea
                name="deliveryNotes"
                rows="4"
                value={values.deliveryNotes}
                onChange={onChange}
              />
            </label>
          </>
        )}
      </div>

      {!isPickup && rates.length === 0 && (
        <p className="form-error">
          Delivery districts have not been configured yet. Please contact ZEVI.
        </p>
      )}

      <button
        className="gold-button checkout-next"
        type="submit"
        disabled={!isPickup && rates.length === 0}
      >
        Continue to payment
      </button>
    </form>
  );
}

function PaymentStep(props) {
  const {
    methods,
    selected,
    onSelect,
    proof,
    proofPreview,
    onProof,
    error,
    onBack,
    onContinue
  } = props;
  const activeMethod = methods.find((method) => method.method === selected);
  const requiresProof = ['bank_transfer', 'qr_payment'].includes(selected);

  return (
    <section className="checkout-card">
      <div className="checkout-card-heading">
        <SafeIcon icon={FiCreditCard} />
        <div>
          <span>Step 02</span>
          <h2>Payment Method</h2>
        </div>
      </div>

      <div className="payment-option-list">
        {methods.map((method) => (
          <label
            className={`payment-option ${selected === method.method ? 'selected' : ''}`}
            key={method.id}
          >
            <input
              type="radio"
              name="paymentMethod"
              checked={selected === method.method}
              onChange={() => onSelect(method.method)}
            />
            <span>
              <strong>
                {method.method === 'cash_on_delivery'
                  ? 'Cash on Delivery'
                  : method.method === 'bank_transfer'
                    ? 'Bank Transfer'
                    : 'QR Payment'}
              </strong>
              <small>
                {method.method === 'cash_on_delivery'
                  ? 'Pay when your order is delivered.'
                  : method.method === 'bank_transfer'
                    ? 'Transfer to our bank account and upload the receipt.'
                    : 'Scan the QR code and upload the receipt.'}
              </small>
            </span>
          </label>
        ))}
      </div>

      {methods.length === 0 && (
        <div className="checkout-empty">
          No payment method is currently enabled. Please contact ZEVI or ask an
          administrator to enable and save Cash on Delivery, Bank Transfer, or
          QR Payment.
        </div>
      )}

      {activeMethod?.customer_instructions && selected === 'bank_transfer' && (
        <BankInstructionsDisplay instructions={activeMethod.customer_instructions} />
      )}

      {activeMethod?.customer_instructions && selected === 'qr_payment' && (
        <div className="payment-instructions">
          <strong>Payment instructions</strong>
          <p>{activeMethod.customer_instructions}</p>
        </div>
      )}

      {selected === 'qr_payment' && activeMethod?.qr_image_url && (
        <div className="qr-display">
          <img src={activeMethod.qr_image_url} alt="Payment QR code" />
          <span>Scan to pay</span>
        </div>
      )}

      {requiresProof && (
        <label className="proof-upload">
          <SafeIcon icon={FiUpload} />
          <strong>{proof ? 'Replace payment proof' : 'Upload payment proof'}</strong>
          <span>JPG, PNG, or WebP. Max 5MB.</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => onProof(event.target.files?.[0] || null)}
          />
          {proof && <em>{proof.name}</em>}
        </label>
      )}

      {proofPreview && (
        <img className="proof-preview" src={proofPreview} alt="Proof preview" />
      )}
      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="checkout-actions">
        <button className="outline-button" onClick={onBack}>Back</button>
        <button
          className="gold-button"
          onClick={onContinue}
          disabled={!selected || (requiresProof && !proof)}
        >
          Review order
        </button>
      </div>
    </section>
  );
}

function ReviewStep(props) {
  const {
    customer,
    district,
    deliveryOption,
    storePickup,
    items,
    subtotal,
    deliveryAmount,
    paymentMethod,
    proof,
    promoCode,
    onBack,
    onConfirm,
    submitting,
    error
  } = props;

  const isPickup = deliveryOption === 'pickup';

  return (
    <section className="checkout-card">
      <div className="checkout-card-heading">
        <SafeIcon icon={FiPackage} />
        <div>
          <span>Step 03</span>
          <h2>Review Your Order</h2>
        </div>
      </div>

      <div className="checkout-review-products">
        {items.map((item) => (
          <article key={item.key}>
            <img src={item.image} alt={item.imageAlt} />
            <div>
              <h3>{item.name}</h3>
              <p>
                {item.variantName || item.colorName || ''}
                {item.sku && ` · ${item.sku}`}
                {' · Quantity '}{item.quantity}
              </p>
            </div>
            <strong>{formatCurrency(item.price * item.quantity)}</strong>
          </article>
        ))}
      </div>

      <div className="checkout-review-grid">
        <div>
          <span>{isPickup ? 'Pickup from' : 'Deliver to'}</span>
          <strong>{customer.fullName || customer.email}</strong>
          {isPickup ? (
            <>
              <p>{storePickup?.address}</p>
              {storePickup?.city && <p>{storePickup.city}</p>}
            </>
          ) : (
            <p>{customer.addressLine1}, {customer.city}, {district?.region}</p>
          )}
          <p>{customer.email} · {customer.countryCode}{customer.phone}</p>
        </div>
        <div>
          <span>Payment</span>
          <strong>
            {paymentMethod === 'cash_on_delivery'
              ? 'Cash on Delivery'
              : paymentMethod === 'bank_transfer'
                ? 'Bank Transfer'
                : 'QR Payment'}
          </strong>
          <p>{proof ? `Proof: ${proof.name}` : 'No proof required'}</p>
        </div>
      </div>

      {promoCode && (
        <p className="checkout-promo-review">
          Promo code <strong>{promoCode}</strong> will be verified securely.
        </p>
      )}

      <div className="summary-line">
        <span>Subtotal</span>
        <strong>{formatCurrency(subtotal)}</strong>
      </div>
      {!isPickup && (
        <div className="summary-line">
          <span>Delivery to {district?.region}</span>
          <strong>{formatCurrency(deliveryAmount)}</strong>
        </div>
      )}
      {isPickup && (
        <div className="summary-line">
          <span>Delivery</span>
          <strong>Free (Store Pickup)</strong>
        </div>
      )}
      <div className="checkout-review-total">
        <span>Current total</span>
        <strong>{formatCurrency(subtotal + (isPickup ? 0 : deliveryAmount))}</strong>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="checkout-actions">
        <button className="outline-button" onClick={onBack}>Back</button>
        <button
          className="gold-button"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? 'Confirming securely…' : 'Confirm order'}
        </button>
      </div>
    </section>
  );
}

function CheckoutPage() {
  const location = useLocation();
  const { items, subtotal, clearCart } = useCart();
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(initialCustomer);
  const [deliveryOption, setDeliveryOption] = useState('home');
  const [configuration, setConfiguration] = useState({
    paymentMethods: [],
    deliveryRates: [],
    storePickup: { enabled: false },
    settings: {}
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [proof, setProof] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [order, setOrder] = useState(null);
  const [proofWarning, setProofWarning] = useState('');
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const promoCode = location.state?.promoCode || '';

  const loadConfiguration = async () => {
    setStatus('loading');
    try {
      const result = await getCheckoutConfiguration();
      setConfiguration(result);
      setPaymentMethod(result.paymentMethods[0]?.method || '');
      if (!result.storePickup?.enabled) {
        setDeliveryOption('home');
      }
      setStatus('ready');
    } catch (err) {
      console.error('Failed to load checkout configuration:', err);
      setStatus('error');
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  useEffect(() => {
    if (order) {
      window.scrollTo(0, 0);
    }
  }, [order]);

  const selectedPayment = useMemo(
    () => configuration.paymentMethods.find(
      (method) => method.method === paymentMethod
    ),
    [configuration.paymentMethods, paymentMethod]
  );

  const selectedDistrict = useMemo(
    () => configuration.deliveryRates.find(
      (rate) => rate.id === customer.region
    ),
    [configuration.deliveryRates, customer.region]
  );

  const deliveryAmount = Number(selectedDistrict?.rate || 0);
  const isPickup = deliveryOption === 'pickup';

  const updateCustomer = (event) => {
    setCustomer((current) => ({
      ...current,
      [event.target.name]: event.target.value
    }));
  };

  const autofillTestCustomer = () => {
    setCustomer({
      ...initialCustomer,
      email: 'zevi.test@example.com',
      fullName: 'ZEVI Test Customer',
      phone: '771234567',
      addressLine1: '25 Test Avenue',
      addressLine2: 'Apartment 4',
      city: 'Colombo',
      region: configuration.deliveryRates[0]?.id || '',
      deliveryNotes: 'Test checkout'
    });
    setDeliveryOption('home');
  };

  const chooseProof = (file) => {
    setError('');
    const validationError = validateProof(file, selectedPayment);
    if (validationError) {
      setProof(null);
      setProofPreview(null);
      setError(validationError);
      return;
    }
    setProof(file);
    setProofPreview(URL.createObjectURL(file));
  };

  const confirmOrder = async () => {
    setError('');
    setProofWarning('');
    setStatus('submitting');

    if (items.some((item) => !item.productId)) {
      setStatus('ready');
      setError('Refresh the catalogue before confirming your order.');
      return;
    }

    const cleanPhone = customer.phone.replace(/\D/g, '').replace(/^0+/, '');
    const shippingAddress = isPickup
      ? {
          address_line_1: 'Store Pickup',
          address_line_2: '',
          city: configuration.storePickup?.city || '',
          region: 'pickup',
          country: 'Sri Lanka'
        }
      : {
          address_line_1: customer.addressLine1.trim(),
          address_line_2: customer.addressLine2.trim(),
          city: customer.city.trim(),
          region: selectedDistrict?.region || '',
          country: 'Sri Lanka'
        };

    const payload = {
      email: customer.email.trim(),
      full_name: isPickup ? customer.email.split('@')[0] : customer.fullName.trim(),
      phone: `${customer.countryCode}${cleanPhone}`,
      shipping_address: shippingAddress,
      delivery_notes: isPickup
        ? 'Store pickup'
        : customer.deliveryNotes.trim(),
      payment_method: paymentMethod,
      promo_code: promoCode || null,
      shipping_rate_id: isPickup ? null : (selectedDistrict?.id || null),
      delivery_option: deliveryOption,
      items: items.map((item) => ({
        product_id: item.productId,
        variant_id: item.variantId,
        quantity: item.quantity
      }))
    };

    try {
      const result = await createOrder(payload, crypto.randomUUID());

      if (['bank_transfer', 'qr_payment'].includes(paymentMethod) && proof) {
        try {
          const proofData = await uploadGuestProof(proof);
          await submitGuestProof(
            result.order_number,
            result.lookup_token,
            proofData
          );
        } catch (proofError) {
          console.error('Proof submission failed:', proofError);
          setProofWarning(
            'Your order was placed successfully, but the payment proof could not be linked automatically. ' +
            'Please contact ZEVI via WhatsApp with your order number and proof of payment.'
          );
        }
      }

      setOrder(result);
      clearCart();
      setStatus('ready');
    } catch (requestError) {
      setError(requestError.message);
      setStatus('ready');
    }
  };

  if (order) {
    return (
      <main className="checkout-page">
        <section className="checkout-success">
          <span className="checkout-success-icon">
            <SafeIcon icon={FiCheck} />
          </span>
          <span className="eyebrow">Order Confirmed</span>
          <h1>Thank You</h1>
          <p>Your ZEVI order has been submitted securely.</p>
          {proofWarning && (
            <div className="checkout-proof-warning">
              <strong>Payment proof notice</strong>
              <p>{proofWarning}</p>
            </div>
          )}
          <dl>
            <div>
              <dt>Order number</dt>
              <dd>{order.order_number}</dd>
            </div>
            <div>
              <dt>Delivery</dt>
              <dd>
                {isPickup
                  ? `Store Pickup — ${configuration.storePickup?.address}`
                  : selectedDistrict?.region}
              </dd>
            </div>
          </dl>
          <div className="checkout-success-actions">
            <Link className="gold-button" to="/shop">Continue shopping</Link>
            <Link className="outline-button" to="/order-tracking">
              Track order
            </Link>
          </div>
        </section>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="checkout-page">
        <section className="checkout-empty-page">
          <span className="eyebrow">Secure Checkout</span>
          <h1>Your Bag Is Empty</h1>
          <p>Add a ZEVI saree before beginning checkout.</p>
          <Link className="gold-button" to="/shop">View collection</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <header className="checkout-heading">
        <span className="eyebrow">Secure ZEVI Checkout</span>
        <h1>Complete Your Order</h1>
        <p>Your final price, stock, and delivery amount are verified securely.</p>
      </header>

      <CheckoutProgress step={step} />

      {status === 'loading' && (
        <div className="checkout-loading">Loading checkout options…</div>
      )}
      {status === 'error' && (
        <div className="checkout-loading checkout-load-error">
          Checkout configuration is temporarily unavailable.
          <button
            className="gold-button"
            onClick={loadConfiguration}
            style={{ marginTop: 16 }}
          >
            Retry
          </button>
        </div>
      )}

      {status !== 'loading' && status !== 'error' && (
        <div className="checkout-layout">
          <div className="checkout-main">
            {step === 1 && (
              <CustomerStep
                values={customer}
                rates={configuration.deliveryRates}
                deliveryOption={deliveryOption}
                storePickup={configuration.storePickup}
                onChange={updateCustomer}
                onDeliveryOptionChange={setDeliveryOption}
                onContinue={(event) => {
                  event.preventDefault();
                  setStep(2);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onRefreshConfig={loadConfiguration}
                onAutofill={autofillTestCustomer}
              />
            )}
            {step === 2 && (
              <PaymentStep
                methods={configuration.paymentMethods}
                selected={paymentMethod}
                onSelect={(method) => {
                  setPaymentMethod(method);
                  setProof(null);
                  setProofPreview(null);
                  setError('');
                }}
                proof={proof}
                proofPreview={proofPreview}
                onProof={chooseProof}
                error={error}
                onBack={() => setStep(1)}
                onContinue={() => {
                  setStep(3);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}
            {step === 3 && (
              <ReviewStep
                customer={customer}
                district={selectedDistrict}
                deliveryOption={deliveryOption}
                storePickup={configuration.storePickup}
                items={items}
                subtotal={subtotal}
                deliveryAmount={deliveryAmount}
                paymentMethod={paymentMethod}
                proof={proof}
                promoCode={promoCode}
                onBack={() => setStep(2)}
                onConfirm={confirmOrder}
                submitting={status === 'submitting'}
                error={error}
              />
            )}
          </div>

          <aside className="checkout-sidebar">
            <span className="eyebrow">Your Order</span>
            <h2>Order Summary</h2>
            {items.map((item) => (
              <div className="checkout-mini-product" key={item.key}>
                <img src={item.image} alt={item.imageAlt} />
                <div>
                  <strong>{item.name}</strong>
                  <span>
                    {item.variantName || item.colorName || ''}
                    {' · Qty '}{item.quantity}
                  </span>
                </div>
                <b>{formatCurrency(item.price * item.quantity)}</b>
              </div>
            ))}
            <div className="summary-line">
              <span>Subtotal</span>
              <strong>{formatCurrency(subtotal)}</strong>
            </div>
            <div className="summary-line">
              <span>Delivery</span>
              <strong>
                {isPickup
                  ? 'Free'
                  : selectedDistrict
                    ? formatCurrency(deliveryAmount)
                    : 'Select district'}
              </strong>
            </div>
            <div className="total-row">
              <span>Current total</span>
              <strong>
                {formatCurrency(subtotal + (isPickup ? 0 : deliveryAmount))}
              </strong>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}

export default CheckoutPage;